import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingCredentialsPanel.scss';

import { registerBrandUser } from '../../../Actions/OnboardingActions';
import { doesUserExist as doesUserExistAPI } from '../../../APIClient/users';
import { isValidEmail, isValidPassword } from '../../../Helpers/formatting';
import { getEmail } from '../../../Helpers/user_helpers';

import Tooltip from '../../General/Tooltip';
import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';

const BrandOnboardingCredentialsPanel = props => {
  const { user } = props;
  const [emailExists, setEmailExists] = useState(false);
  const hasGoneFurtherThanThisPanel = props.sections[1].panels.some(p => p.isComplete);

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      name: 'email',
      label: 'Email*',
      type: 'email',
      value: getEmail(user) || '',
      isFieldValid: value => isValidEmail(value),
      isValid: true,
      errorMessage: 'Please enter a valid email',
      disabled: hasGoneFurtherThanThisPanel
    },
    {
      name: 'password',
      label: 'Password*',
      type: 'password',
      value: '',
      isFieldValid: value => value?.length >= 8,
      isValid: true,
      errorMessage: 'Password must be at least 8 characters long.',
      disabled: hasGoneFurtherThanThisPanel
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password*',
      type: 'password',
      value: '',
      isFieldValid: () => true,
      isValid: true,
      errorMessage: 'Your passwords do not match.',
      disabled: hasGoneFurtherThanThisPanel
    }
  ]);

  const canGoToNextPanel = () => fields.every(field => !!field.value) || hasGoneFurtherThanThisPanel;
  const validateFields = async () => {
    let firstErrorMessage = null;
    props.setIsLoading(true);

    let emailExists = false;
    const email = fields.find(field => field.name === 'email').value;
    const emailExistsResponse = email && (await doesUserExistAPI({ email }));
    emailExists = emailExistsResponse?.exists;
    setEmailExists(emailExists);

    const newFields = fields.map(field => {
      let isValidField = field.isFieldValid(field.value, field);
      if (field.name === 'email' && emailExists) {
        isValidField = false;
        firstErrorMessage = 'This email is already in use';
      } else if (field.name === 'confirmPassword') {
        isValidField = field.value === fields.find(f => f.name === 'password').value;
        if (!isValidField) firstErrorMessage = field.errorMessage;
      }

      if (!isValidField) firstErrorMessage = firstErrorMessage || field.errorMessage;
      return { ...field, isValid: isValidField };
    });

    setFields(newFields);
    props.setIsLoading(false);
    return firstErrorMessage;
  };

  const registerCredentials = async () => {
    const [email, password, confirmPassword] = fields.map(f => f.value);
    if (props.isLoading) return;
    else if (hasGoneFurtherThanThisPanel) return props.goToNextPanel();

    const errorMessage = await validateFields();
    if (errorMessage) return window.ALERT.error(errorMessage);

    if (!isValidEmail(email)) return window.ALERT.error('Please enter a valid email address.');
    else if (!isValidPassword(password)) return window.ALERT.error('Please enter a valid password.');
    else if (password !== confirmPassword) return window.ALERT.error('Please make sure your passwords match.');

    props.setIsLoading(true);
    try {
      const registrationResponse = await props.registerBrandUser({ email, password });
      if (!registrationResponse.success) throw new Error(registrationResponse);
      window.ALERT.success(`${email} has been registered successfully!`);
      props.goToNextPanel();
    } catch (e) {
      if (e?.response?.data?.error) window.ALERT.error(e.response.data.error);
      else window.ALERT.error('An error occurred while registering. Please try again.');

      props.setIsLoading(false);
    }
  };

  useEffect(() => {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (field.isValid) continue;

      const isValidField = field.isFieldValid(field.value, field);
      if (isValidField) {
        setFields(fields => {
          const newFields = [...fields];
          newFields[i].isValid = true;
          return newFields;
        });
      }
    }
  }, [fields]);

  return (
    <div className='brand-onboarding-credentials-panel-outer'>
      <div className={cn('brand-onboarding-credentials-panel-inner', { isLoading: props.isLoading })}>
        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner'>
                <div className='brand-onboarding-graphic-container-stat'>
                  <div className='brand-onboarding-graphic-container-stat-label'>Our Numbers</div>
                  <div className='brand-onboarding-graphic-container-stat-number'>{props.featuredStat.number}+</div>
                  <div className='brand-onboarding-graphic-container-stat-description'>{props.featuredStat.description}</div>
                </div>

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>

              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>

            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Log in + Sign up</div>
                <div className='brand-onboarding-subtitle-text'>
                  Store these credentials securely as they will be used to log in to your ShopMy account and access your dashboard. If you exit this
                  page you can login and resume your onboarding progress at any time.
                </div>

                {fields.map((field, index) => (
                  <div key={index} className='brand-onboarding-input-container'>
                    <Tooltip
                      message={
                        hasGoneFurtherThanThisPanel
                          ? 'You cannot update this as you have already registered'
                          : field.name === 'email' && emailExists
                          ? 'This email is already in use'
                          : field.isValid
                          ? ''
                          : field.errorMessage
                      }
                    >
                      {
                        <input
                          className={cn('brand-onboarding-input', {
                            invalid: !field.isValid || (field.name === 'email' && emailExists),
                            filled: !!field.value
                          })}
                          type={field.type}
                          placeholder={field.label}
                          id={field.name}
                          value={field.value}
                          disabled={field.disabled}
                          onChange={e => {
                            const { value } = e.target;
                            setFields(prev => updateField(prev, index, { value }));
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              registerCredentials();
                            }

                            if (e.key === 'Escape') {
                              e.preventDefault();
                              const newFields = [...fields];
                              newFields[index].value = '';
                              setFields(newFields);
                            }
                          }}
                        />
                      }
                    </Tooltip>

                    {!!field.supportText && (
                      <div className={cn('creator-application-input-support-text', { alignRight: !field.inputValueAlignRight })}>
                        {field.supportText}
                      </div>
                    )}
                  </div>
                ))}

                <div className='brand-onboarding-pagination-buttons'>
                  <button
                    className={cn('brand-onboarding-button primary', { disabled: props.isLoading || !canGoToNextPanel() })}
                    onClick={registerCredentials}
                    disabled={props.isLoading || !canGoToNextPanel()}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingCredentialsPanel.propTypes = {
  goToNextPanel: PropTypes.func.isRequired,
  goToPreviousPanel: PropTypes.func.isRequired,
  registerBrandUser: PropTypes.func.isRequired,
  backgroundImage: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  user: state.user
});

export default connect(mapStateToProps, {
  registerBrandUser
})(withRouter(BrandOnboardingCredentialsPanel));
